import { Injectable, inject } from '@angular/core';
import { NativeBridgeService } from './native-bridge.service';
import { WebViewAction } from '../../enums/webview-action';

/** Actions comprises par le canal natif. */
export type PurchaseAction = 'products' | 'buy' | 'restore' | 'manage';

/** Issue rapportée par le magasin. */
export type PurchaseStatus =
  'ok' | 'cancelled' | 'pending' | 'unavailable' | 'error';

/** Prix d'une formule tel que le magasin l'annonce pour cet appareil. */
export interface StorePriceReply {
  plan: string;
  amount: number;
  currency: string;
  /** Déjà formaté par le magasin. À afficher tel quel. */
  priceString: string;
  /** Essai gratuit accordé par le magasin à cet appareil (coque 1.5.1+). */
  trialDays?: number;
}

export interface PurchaseReply {
  requestId?: string;
  status: PurchaseStatus;
  /** Le magasin a confirmé un achat. Ne débloque RIEN par lui-même. */
  purchased?: boolean;
  message?: string;
  products?: StorePriceReply[];
}

/**
 * Délais d'attente, et ils ne peuvent pas être les mêmes.
 *
 * Une demande de prix doit rendre la main vite : le paywall s'affiche derrière.
 * Un achat, non — la feuille du magasin reste ouverte le temps qu'il faut :
 * saisie d'un mot de passe, Face ID, ou « Demander à acheter » qui attend
 * l'accord d'un parent. Couper au bout de dix secondes annulerait un achat en
 * cours du point de vue de l'app, alors que le magasin, lui, le mènerait à bien.
 */
const TIMEOUT_MS: Record<PurchaseAction, number> = {
  products: 15_000,
  buy: 600_000,
  restore: 120_000,
  manage: 30_000,
};

/**
 * Pont vers l'achat intégré natif.
 *
 * Chaque demande porte un `requestId` et la réponse le renvoie. Sans lui, une
 * demande de prix lancée pendant un achat verrait sa réponse résoudre l'attente
 * de l'achat — et l'écran annoncerait « acheté » sur une simple consultation de
 * tarifs. Les autres canaux de l'app sont à sens unique ; celui-ci fait un
 * aller-retour, donc il lui faut une correspondance.
 */
@Injectable({ providedIn: 'root' })
export class PurchaseBridgeService {
  private readonly bridge = inject(NativeBridgeService);

  private sequence = 0;
  private readonly pending = new Map<string, (reply: PurchaseReply) => void>();
  private callbackInstalled = false;

  /** L'app native expose-t-elle l'achat intégré ? */
  isAvailable(): boolean {
    return this.bridge.has(WebViewAction.purchase);
  }

  async send(
    action: PurchaseAction,
    extra: Record<string, unknown> = {},
  ): Promise<PurchaseReply> {
    if (!this.isAvailable()) {
      return { status: 'unavailable', message: 'purchase channel absent' };
    }
    this.installCallback();

    const requestId = `p-${++this.sequence}`;
    return new Promise<PurchaseReply>((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        // On n'invente pas d'issue : c'est une panne, pas un renoncement.
        resolve({ status: 'error', message: `timeout on ${action}` });
      }, TIMEOUT_MS[action]);

      this.pending.set(requestId, (reply) => {
        clearTimeout(timer);
        resolve(reply);
      });

      this.bridge.post(WebViewAction.purchase, {
        action,
        requestId,
        ...extra,
      });
    });
  }

  /**
   * Installe `window.onPurchase`, que le natif appelle avec une CHAÎNE JSON.
   *
   * Une chaîne et non un objet : un message d'erreur du magasin contenant une
   * apostrophe casserait le JavaScript injecté côté natif.
   */
  private installCallback(): void {
    if (this.callbackInstalled) return;
    this.bridge.listen('onPurchase', (raw: unknown) => {
      let reply: PurchaseReply;
      try {
        reply =
          typeof raw === 'string' ? JSON.parse(raw) : (raw as PurchaseReply);
      } catch {
        console.warn('onPurchase : charge illisible', raw);
        return;
      }
      const requestId = reply?.requestId;
      if (!requestId) {
        console.warn('onPurchase : réponse sans requestId', reply);
        return;
      }
      const resolver = this.pending.get(requestId);
      // Réponse en retard, après le délai : elle n'a plus personne à résoudre.
      if (!resolver) return;
      this.pending.delete(requestId);
      resolver(reply);
    });
    this.callbackInstalled = true;
  }
}
